/* eslint-disable no-alert */
import React, { useState } from 'react';
import { Table, Input, Button, Spinner } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileAlt } from '@fortawesome/free-solid-svg-icons';
import usePendingAction from '../hooks/usePendingAction';
import ModalUploadPOItems from '../modals/ModalUploadPOItems';
import ModalPreviewPOItemProof from '../modals/ModalPreviewPOItemProof';
import {
  createPoItem,
  updatePoItem,
  deletePoItem,
  updatePoItemProofs,
} from '../api';
import { alertError, formatRp, parsePrice } from '../utils';

const NEW_ROW = ''; // upload/preview target id meaning "the not-yet-added row"
const ZERO_PRICE = { display: '0', value: 0 };
const EMPTY_ITEM = { details: '', price: ZERO_PRICE, files: [] };

const ICON_STYLE = {
  width: '25px',
  height: '25px',
  color: '#6c757d',
  cursor: 'pointer',
};
const BUTTON = { width: '100px' };

const ProofIcon = ({ files, onClick }) =>
  files?.length ? (
    <FontAwesomeIcon icon={faFileAlt} style={ICON_STYLE} onClick={onClick} />
  ) : (
    'N/A'
  );

const Actions = ({ children }) => (
  <td>
    <div className="d-flex justify-content-center">{children}</div>
  </td>
);

/**
 * The PO items table: view/edit/delete rows, upload proofs, add a new row.
 * Every change hits the backend and then calls `onChanged` so the parent
 * reloads items and total price.
 */
const PoItemsTable = ({ poId, items, readOnly, onChanged }) => {
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(EMPTY_ITEM);
  const [newItem, setNewItem] = useState(EMPTY_ITEM);
  const [showErrors, setShowErrors] = useState(false);
  const [uploadTargetId, setUploadTargetId] = useState(null);
  const [previewTargetId, setPreviewTargetId] = useState(null);
  const { isPending, run } = usePendingAction();

  const clearNewItem = () => {
    setNewItem(EMPTY_ITEM);
    setShowErrors(false);
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditDraft({
      details: item.details,
      price: {
        display: item.price.toLocaleString('en-US'),
        value: item.price,
      },
    });
  };

  const request = (key, id, call) =>
    run(key, id, async () => {
      try {
        await call();
        await onChanged();
      } catch (err) {
        alertError(err);
      }
    });

  const handleAdd = () => {
    if (!newItem.details || !newItem.price.value) {
      alert('Please Insert Details and Price !');
      setShowErrors(true);
      return;
    }
    if (!window.confirm('Are you sure you want to add these details ?')) return;
    const item = newItem;
    clearNewItem();
    request('add', NEW_ROW, () =>
      createPoItem({
        poHeaderId: poId,
        details: item.details,
        price: item.price.value,
        files: item.files,
      }),
    );
  };

  const handleSave = (item) => {
    const draft = editDraft;
    setEditingId(null);
    request('save', item.id, () =>
      updatePoItem(item.id, {
        details: draft.details || item.details,
        price: draft.price.value || item.price,
      }),
    );
  };

  const handleDelete = (id) => {
    if (!window.confirm('Are you sure want to delete these details ?')) return;
    request('delete', id, () => deletePoItem(id));
  };

  const handleUpload = async (files) => {
    if (uploadTargetId === NEW_ROW) {
      setNewItem((current) => ({ ...current, files }));
      return;
    }
    await updatePoItemProofs(uploadTargetId, files);
    await onChanged();
  };

  const previewFiles =
    previewTargetId === NEW_ROW
      ? newItem.files
      : items.find((item) => item.id === previewTargetId)?.files || [];

  const renderRow = (item, index) => {
    if (item.id === editingId) {
      return (
        <tr key={item.id}>
          <td>#</td>
          <td>
            <Input
              type="text"
              placeholder="Input Details"
              value={editDraft.details}
              onChange={(e) =>
                setEditDraft({ ...editDraft, details: e.target.value })
              }
            />
          </td>
          <td>
            <Input
              type="text"
              placeholder="0"
              value={editDraft.price.display}
              onChange={(e) =>
                setEditDraft({
                  ...editDraft,
                  price: parsePrice(e.target.value),
                })
              }
            />
          </td>
          <td align="center">
            <ProofIcon
              files={item.files}
              onClick={() => setPreviewTargetId(item.id)}
            />
          </td>
          {!readOnly && (
            <Actions>
              <Button
                color="primary"
                className="btn text-center"
                style={BUTTON}
                onClick={() => setUploadTargetId(item.id)}
              >
                Upload
              </Button>
              <Button
                className="btn btn-primary text-center ml-2"
                style={BUTTON}
                onClick={() => handleSave(item)}
              >
                {isPending('save', item.id) ? (
                  <Spinner size="sm" color="white" />
                ) : (
                  'Save'
                )}
              </Button>
              <Button
                className="btn btn-danger text-center ml-2"
                style={BUTTON}
                onClick={() => setEditingId(null)}
              >
                Cancel
              </Button>
            </Actions>
          )}
        </tr>
      );
    }
    return (
      <tr key={item.id}>
        <td>{index + 1}</td>
        <td>{item.details}</td>
        <td>{formatRp(item.price)}</td>
        <td align="center">
          <ProofIcon
            files={item.files}
            onClick={() => setPreviewTargetId(item.id)}
          />
        </td>
        {!readOnly && (
          <Actions>
            <Button
              color="primary"
              className="btn text-center"
              style={BUTTON}
              onClick={() => setUploadTargetId(item.id)}
            >
              Upload
            </Button>
            <Button
              className="btn btn-primary text-center ml-2"
              style={BUTTON}
              onClick={() => startEdit(item)}
            >
              Edit
            </Button>
            <Button
              className="btn btn-danger text-center ml-2"
              style={BUTTON}
              onClick={() => handleDelete(item.id)}
            >
              {isPending('delete', item.id) ? (
                <Spinner size="sm" color="white" />
              ) : (
                'Delete'
              )}
            </Button>
          </Actions>
        )}
      </tr>
    );
  };

  return (
    <>
      <Table striped bordered responsive hover>
        <thead
          className="font-weight-bold"
          style={{ fontSize: '14px', color: '#505050' }}
        >
          <tr>
            <th>#</th>
            <th>Details</th>
            <th>Price</th>
            <th>Proof</th>
            {!readOnly && <th className="text-center">Action</th>}
          </tr>
        </thead>
        <tbody>{items.map(renderRow)}</tbody>
        {!readOnly && (
          <tfoot>
            <tr>
              <td>#</td>
              <td>
                <Input
                  type="text"
                  placeholder="Input Details"
                  name="details"
                  value={newItem.details}
                  invalid={showErrors && !newItem.details}
                  onChange={(e) =>
                    setNewItem({ ...newItem, details: e.target.value })
                  }
                />
              </td>
              <td>
                <Input
                  type="text"
                  placeholder="0"
                  name="price"
                  value={newItem.price.display}
                  invalid={showErrors && !newItem.price.value}
                  onChange={(e) =>
                    setNewItem({
                      ...newItem,
                      price: parsePrice(e.target.value),
                    })
                  }
                />
              </td>
              <td align="center">
                <ProofIcon
                  files={newItem.files}
                  onClick={() => setPreviewTargetId(NEW_ROW)}
                />
              </td>
              <Actions>
                <Button
                  className="btn btn-success text-center ml-2"
                  style={{ ...BUTTON, backgroundColor: 'rgb(50, 178, 128)' }}
                  onClick={handleAdd}
                >
                  {isPending('add', NEW_ROW) ? (
                    <Spinner size="sm" color="white" />
                  ) : (
                    'Add'
                  )}
                </Button>
                <Button
                  className="btn btn-danger ml-2"
                  style={BUTTON}
                  onClick={clearNewItem}
                >
                  Clear
                </Button>
              </Actions>
            </tr>
          </tfoot>
        )}
      </Table>

      <ModalUploadPOItems
        isOpen={uploadTargetId !== null}
        handleUploadImage={handleUpload}
        toggle={() => setUploadTargetId(null)}
      />
      <ModalPreviewPOItemProof
        isOpen={previewTargetId !== null}
        files={previewFiles}
        type={previewTargetId === NEW_ROW ? 'add' : undefined}
        toggle={() => setPreviewTargetId(null)}
      />
    </>
  );
};

export default PoItemsTable;
